import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Badge } from '../../components/ui/badge';
import { Radio, Music, Play, Pause, Volume2, VolumeX, Heart, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MusicPlayer = () => {
  const [loading, setLoading] = useState(true);
  const [stations, setStations] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(50);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('reddi_token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;

      const [stationsRes, prefsRes] = await Promise.all([
        fetch(`${baseUrl}/api/music/stations`),
        fetch(`${baseUrl}/api/driver/music/preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const stationsData = await stationsRes.json();
      const prefsData = await prefsRes.json();

      setStations(stationsData);
      setPreferences(prefsData);
      setVolume(prefsData.volume || 50);
    } catch (error) {
      console.error('Error loading music data:', error);
    } finally {
      setLoading(false);
    }
  };

  const playStation = (station) => {
    setCurrentStation(station);
    setIsPlaying(true);
    toast.success(`Now playing: ${station.name}`);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (value) => {
    setVolume(value[0]);
    savePreferences({ volume: value[0] });
  };

  const toggleFavorite = async (stationId) => {
    const favorites = preferences?.favorite_stations || [];
    const newFavorites = favorites.includes(stationId)
      ? favorites.filter(id => id !== stationId)
      : [...favorites, stationId];
    
    setPreferences({ ...preferences, favorite_stations: newFavorites });
    await savePreferences({ favorite_stations: newFavorites });
  };

  const savePreferences = async (updates) => {
    try {
      const token = localStorage.getItem('reddi_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/driver/music/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...preferences,
          ...updates
        })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const connectSpotify = () => {
    // In production, this would redirect to Spotify OAuth
    toast.info('Spotify integration coming soon!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  const genreColors = {
    pop: 'bg-pink-500',
    rock: 'bg-red-600',
    hiphop: 'bg-purple-600',
    jazz: 'bg-blue-600',
    electronic: 'bg-cyan-500',
    country: 'bg-amber-600',
    chill: 'bg-teal-500',
    talk: 'bg-slate-600'
  };

  return (
    <div className="p-6" data-testid="music-player-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Music & Radio
        </h1>

        {/* Now Playing */}
        {currentStation && (
          <Card className="mb-6 bg-gradient-to-br from-slate-900 to-slate-800 border-0 text-white overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 ${genreColors[currentStation.genre]} rounded-xl flex items-center justify-center`}>
                    <Radio className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm uppercase tracking-wide">Now Playing</p>
                    <p className="text-2xl font-display font-bold">{currentStation.name}</p>
                    <Badge className="mt-1 bg-white/20 text-white border-0 capitalize">
                      {currentStation.genre}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Volume Control */}
                  <div className="flex items-center gap-2 w-32">
                    <button onClick={toggleMute} className="text-white/60 hover:text-white">
                      {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                    <Slider
                      value={[isMuted ? 0 : volume]}
                      onValueChange={handleVolumeChange}
                      max={100}
                      step={1}
                      className="w-24"
                    />
                  </div>
                  
                  {/* Play/Pause */}
                  <Button
                    size="icon"
                    onClick={togglePlay}
                    className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-700"
                  >
                    {isPlaying ? (
                      <Pause className="w-6 h-6" />
                    ) : (
                      <Play className="w-6 h-6 ml-1" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Visualizer (simulated) */}
              {isPlaying && (
                <div className="flex items-end justify-center gap-1 h-12 mt-4">
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="w-2 bg-red-500 rounded-t animate-pulse"
                      style={{
                        height: `${Math.random() * 100}%`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Spotify Connect */}
        <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <Music className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">Spotify</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {preferences?.spotify_connected 
                      ? 'Connected - Play your playlists'
                      : 'Connect to play your music'}
                  </p>
                </div>
              </div>
              <Button
                onClick={connectSpotify}
                className={preferences?.spotify_connected 
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-slate-900 hover:bg-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600'
                }
              >
                {preferences?.spotify_connected ? (
                  <>Connected</>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Favorites */}
        {preferences?.favorite_stations?.length > 0 && (
          <Card className="mb-6 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-600 fill-red-600" />
                Favorite Stations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {stations.filter(s => preferences.favorite_stations.includes(s.id)).map((station) => (
                  <button
                    key={station.id}
                    onClick={() => playStation(station)}
                    className={`
                      p-4 rounded-xl text-left transition-all
                      ${currentStation?.id === station.id
                        ? 'ring-2 ring-red-600 bg-red-50 dark:bg-red-900/20'
                        : 'bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }
                    `}
                  >
                    <div className={`w-10 h-10 ${genreColors[station.genre]} rounded-lg flex items-center justify-center mb-2`}>
                      <Radio className="w-5 h-5 text-white" />
                    </div>
                    <p className="font-medium text-slate-900 dark:text-white text-sm truncate">{station.name}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Stations */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <Radio className="w-5 h-5 text-red-600" />
              Radio Stations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {stations.map((station) => {
                const isFavorite = preferences?.favorite_stations?.includes(station.id);
                const isActive = currentStation?.id === station.id;

                return (
                  <div
                    key={station.id}
                    className={`
                      flex items-center justify-between p-4 rounded-xl transition-all
                      ${isActive
                        ? 'bg-red-50 dark:bg-red-900/20 ring-2 ring-red-600'
                        : 'bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }
                    `}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 ${genreColors[station.genre]} rounded-xl flex items-center justify-center`}>
                        <Radio className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white">{station.name}</p>
                        <Badge variant="outline" className="mt-1 capitalize">
                          {station.genre}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => toggleFavorite(station.id)}
                        className={isFavorite ? 'text-red-600' : 'text-slate-400'}
                      >
                        <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                      </Button>
                      <Button
                        size="icon"
                        onClick={() => playStation(station)}
                        className={isActive && isPlaying
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500'
                        }
                      >
                        {isActive && isPlaying ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5 ml-0.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MusicPlayer;
